import buttonsFavorite from "../../../components/buttons/_favorite.js";

const SP_MQ = "(max-width: 640px)";

// カード配置の余白定数（Leaflet時代の見え方をそのまま踏襲）
const GAP = 14; // ピンとカードの間隔
const PIN_HALF_W = 15; // ピン幅の半分
const PIN_H = 28; // ピンの見かけの高さ
const PAD = 8; // 地図端からの最低余白
const FALLBACK_CARD_W = 320; // 非表示時など offsetWidth が 0 の場合の想定幅
const FALLBACK_CARD_H = 360;

// 文言は地図の上に直接出るため、見た目（半透明グレーの下地）はSCSSの
// .pg-search-map-card__status 側で持たせる
const statusHtml = (message) =>
    `<p class="pg-search-map-card__status">${message}</p>`;

const LOADING_HTML = statusHtml("読み込み中です");

/**
 * ピン押下時に開く物件カードの表示・配置を担当する。
 * 地図ライブラリには依存させず、緯度経度→地図コンテナ内ピクセルの変換だけを
 * projector（setProjector）として外から注入してもらう。
 */
class CardPanel {
    constructor({ mapRoot, cardRoot, cardInner }) {
        this.mapRoot = mapRoot;
        this.cardRoot = cardRoot;
        this.cardInner = cardInner;

        this.projectLatLng = null;
        this.activeLatLng = null;
        this.onClose = null;

        // 非同期取得の追い越し対策。open のたびに採番し、古い応答は捨てる
        this.openToken = 0;

        // ピン操作直後に続けて発火する地図クリックで即閉じしないための抑止フラグ
        this._ignoreMapClick = false;
        this._ignoreMapClickTimer = 0;

        this.#bindCardClose();
        this.#bindViewport();
    }

    // 地図の準備が整ってから注入される（緯度経度 → 地図コンテナ内ピクセル）
    setProjector(projectLatLng) {
        this.projectLatLng = projectLatLng;
    }

    // 閉じたことをマーカー側へ通知して選択状態を解除させる
    setOnClose(callback) {
        this.onClose = callback;
    }

    isOpen() {
        return Boolean(this.cardRoot?.classList.contains("is-open"));
    }

    /**
     * カードを開く。中身は取得待ちのため先に読み込み中表示で開き、
     * 取得後に setContent で差し替える。戻り値は追い越し判定用のトークン。
     */
    open(latLng) {
        if (!this.cardRoot || !this.cardInner) return 0;

        this.openToken += 1;
        this.activeLatLng = latLng;
        this.suppressMapClick();

        this.cardInner.innerHTML = LOADING_HTML;
        this.cardRoot.hidden = false;
        this.cardRoot.classList.add("is-open");

        // 先に仮配置→レイアウト確定後に再配置（角に一瞬出るのを防ぐ）
        this.#positionCard();
        requestAnimationFrame(() => this.#positionCard());

        return this.openToken;
    }

    // サーバーが返した物件カードHTMLを流し込む
    setContent(html, token) {
        if (!this.#isCurrent(token) || !this.cardInner) return;

        this.cardInner.innerHTML = html;
        // 差し込んだカード内の星ボタンを有効化する
        buttonsFavorite();

        this.#positionCard();
        requestAnimationFrame(() => this.#positionCard());
    }

    // 取得失敗時などの文言表示
    setMessage(message, token) {
        if (!this.#isCurrent(token) || !this.cardInner) return;

        this.cardInner.innerHTML = statusHtml(message);
        this.#positionCard();
    }

    close() {
        if (!this.activeLatLng && !this.isOpen()) return;

        this.activeLatLng = null;
        // 閉じた後に届いた応答を無効化する
        this.openToken += 1;

        if (this.cardRoot) {
            this.cardRoot.classList.remove("is-open");
            this.cardRoot.hidden = true;
            this.#clearCardInlinePos();
            delete this.cardRoot.dataset.placement;
        }

        this.onClose?.();
    }

    // 地図の移動・ズーム後に呼ばれ、開いていれば追従させる
    reposition() {
        if (!this.activeLatLng || !this.isOpen()) return;
        this.#positionCard();
    }

    // 開いたまま指し先だけ差し替える。同座標でずらしたピンはズームのたびに
    // 表示位置が変わるので、作り直したピンの位置へカードを付け直す
    moveAnchor(latLng) {
        if (!this.activeLatLng || !this.isOpen()) return;
        this.activeLatLng = latLng;
        this.#positionCard();
    }

    /** ピン操作直後の地図クリックを無視する */
    suppressMapClick() {
        this._ignoreMapClick = true;
        window.clearTimeout(this._ignoreMapClickTimer);
        this._ignoreMapClickTimer = window.setTimeout(() => {
            this._ignoreMapClick = false;
        }, 0);
    }

    // 地図の余白クリックで閉じる（ピン押下に伴うクリックは抑止フラグで無視）
    handleMapBackgroundClick() {
        if (this._ignoreMapClick) return;
        this.close();
    }

    #isCurrent(token) {
        return this.isOpen() && token === this.openToken;
    }

    #isSp() {
        return window.matchMedia(SP_MQ).matches;
    }

    #bindViewport() {
        const onChange = () => this.reposition();
        const mq = window.matchMedia(SP_MQ);
        // Safari 13 以前は addEventListener 非対応のため addListener にフォールバック
        if (mq.addEventListener) mq.addEventListener("change", onChange);
        else mq.addListener(onChange);
        window.addEventListener("resize", onChange);
    }

    #bindCardClose() {
        document
            .querySelectorAll(".js-search-map__card-close")
            .forEach((btn) => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.close();
                });
            });
        // カード内のクリックが背景クリック扱いにならないようにする
        this.cardRoot?.addEventListener("click", (e) => e.stopPropagation());
    }

    #clearCardInlinePos() {
        if (!this.cardRoot) return;
        this.cardRoot.style.left = "";
        this.cardRoot.style.top = "";
        this.cardRoot.style.right = "";
        this.cardRoot.style.bottom = "";
        this.cardRoot.style.transform = "";
    }

    /**
     * SP: 下部固定（CSS）
     * PC: ピン近く。上→下→右→左の順で試し、ピン本体にかぶらないよう gap を確保
     */
    #positionCard() {
        if (!this.cardRoot || !this.mapRoot || !this.activeLatLng) return;

        if (this.#isSp()) {
            this.cardRoot.dataset.placement = "bottom";
            this.#clearCardInlinePos();
            return;
        }

        const point = this.projectLatLng?.(this.activeLatLng);
        // 地図の初回描画前は projection が無く座標を出せない。
        // その場合は次の idle で再配置されるため、ここでは何もしない
        if (!point) return;

        const cardW = this.cardRoot.offsetWidth || FALLBACK_CARD_W;
        const cardH = this.cardRoot.offsetHeight || FALLBACK_CARD_H;
        const mapW = this.mapRoot.clientWidth;
        const mapH = this.mapRoot.clientHeight;

        const fits = (left, top) =>
            left >= PAD &&
            top >= PAD &&
            left + cardW <= mapW - PAD &&
            top + cardH <= mapH - PAD;

        const candidates = [
            // ピンの上（中央寄せ）
            {
                left: point.x - cardW / 2,
                top: point.y - PIN_H - GAP - cardH,
            },
            // ピンの下
            {
                left: point.x - cardW / 2,
                top: point.y + GAP,
            },
            // ピンの右
            {
                left: point.x + PIN_HALF_W + GAP,
                top: point.y - cardH / 2,
            },
            // ピンの左
            {
                left: point.x - PIN_HALF_W - GAP - cardW,
                top: point.y - cardH / 2,
            },
        ];

        const chosen =
            candidates.find((c) => fits(c.left, c.top)) || candidates[0];

        const left = Math.max(PAD, Math.min(chosen.left, mapW - cardW - PAD));
        const top = Math.max(PAD, Math.min(chosen.top, mapH - cardH - PAD));

        this.cardRoot.dataset.placement = "pin";
        this.cardRoot.style.left = `${Math.round(left)}px`;
        this.cardRoot.style.top = `${Math.round(top)}px`;
        this.cardRoot.style.right = "auto";
        this.cardRoot.style.bottom = "auto";
        this.cardRoot.style.transform = "none";
    }
}

export default CardPanel;
